<template>
	<button class="lg:hidden" aria-label="menu" @click="isSideMenuOpened = true">
		<span class="sr-only">Open main menu</span>
		<Bars3Icon class="h-6 w-6 text-white" aria-hidden="true" />
	</button>
	<TransitionRoot :show="isSideMenuOpened" appear as="template">
		<Dialog as="div" class="lg:hidden" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-300 ease-out"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="-translate-x-full"
				enter-to="translate-x-0"
				leave="duration-300 ease-out"
				leave-from="translate-x-0"
				leave-to="-translate-x-full"
			>
				<DialogPanel
					class="fixed inset-y-0 z-50 left-0 w-full overflow-y-auto bg-white pt-6 sm:max-w-sm sm:ring-1 sm:ring-gray-dark"
				>
					<div class="relative h-fit pb-4">
						<div class="px-4 flex items-center justify-between">
							<div>
								<ChevronLeftIcon
									v-if="
										currentNavigationElement || currentChildNavigationElement
									"
									class="cursor-pointer h-6 w-6 text-gray-700"
									aria-hidden="true"
									@click="goBack"
								/>
							</div>
							<div>
								<h4 class="font-medium text-lg">Menu</h4>
							</div>
							<button
								type="button"
								class="-m-2.5 rounded-md p-2.5 text-gray-700 outline-none"
								@click="close"
							>
								<span class="sr-only">Close menu</span>
								<XMarkIcon class="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
						<div class="border-t border-gray-dark mt-6 flow-root relative">
							<div class="divide-y divide-gray-500/10">
								<div>
									<template
										v-if="
											!currentNavigationElement &&
											!currentChildNavigationElement
										"
									>
										<!-- The Menu Node menu, or the old Norce categories until
											the space has one (GF-85). -->
										<div
											v-for="navigationElement in menuItems"
											:key="navigationElement.id"
											class="flex justify-between items-center cursor-pointer border-b border-gray-dark hover:bg-gray"
										>
											<NuxtLink
												:to="hrefOf(navigationElement)"
												class="font-medium px-4 block w-full py-3 text-base leading-7 text-gray-700"
												@click="close"
											>
												{{ navigationElement.name }}
											</NuxtLink>
											<button
												type="button"
												class="py-2 pr-2 pl-4 flex items-center"
												@click="firstNavigate(navigationElement)"
											>
												<ChevronRightIcon
													v-if="navigationElement.children.length"
													class="h-6 w-6"
													aria-hidden="true"
												/>
											</button>
										</div>
										<!-- Old secondary Storyblok menu links. The new menu holds
											its own links, so these only show with the old menu. -->
										<template v-if="!usesNewMenu">
											<StoryblokComponent
												v-for="blok in menuCategories"
												:key="blok._uid"
												:blok="blok"
												class="font-medium cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 block py-3 text-base leading-7 text-gray-700 hover:bg-gray"
												@closeMobileMenu="close"
											/>
										</template>
										<div class="my-6 mx-4 flex flex-col gap-8">
											<template
												v-for="navigationElement in menuCategoriesNorce"
												:key="navigationElement.id"
											>
											</template>
										</div>
									</template>
									<template
										v-else-if="
											currentNavigationElement && !currentChildNavigationElement
										"
									>
										<div
											v-for="navigationElement in currentNavigationElement.children"
											:key="navigationElement.id"
											class="flex justify-between items-center border-b border-gray-dark hover:bg-gray"
										>
											<NuxtLink
												:to="hrefOf(navigationElement)"
												class="font-medium cursor-pointer px-4 block w-full py-3 text-base leading-7"
												@click="close"
											>
												{{ navigationElement.name }}
											</NuxtLink>
											<button
												type="button"
												class="py-2 pr-2 pl-4 flex items-center"
												@click="secondNavigate(navigationElement)"
											>
												<ChevronRightIcon
													v-if="navigationElement.children.length"
													class="h-6 w-6"
													aria-hidden="true"
												/>
											</button>
										</div>
										<div class="my-6 mx-4 flex flex-col gap-8">
											<!-- <template v-for="(navigationElement, index) in currentNavigationElement.Children" :key="navigationElement.id">
                        media
                      </template> -->
										</div>
									</template>
									<template v-else-if="currentChildNavigationElement">
										<NuxtLink
											v-for="navigationElement in currentChildNavigationElement.children"
											:key="navigationElement.id"
											:to="hrefOf(navigationElement)"
											class="cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 block py-3 text-base leading-7 hover:bg-gray"
											@click="close"
										>
											{{ navigationElement.name }}
										</NuxtLink>
									</template>
								</div>
							</div>
						</div>
						<div
							class="pt-2.5 w-full sm:max-w-sm bottom-0 left-0 flex gap-5 items-center bg-gray text-base leading-7"
						>
							<div class="flex flex-col justify-between w-full">
								<div class="flex flex-col w-full text-xs gap-4 items-left p-4">
									<div class="inline-flex gap-1 text-xs items-end">
										<PhoneIcon class="text h-4 w-4 text-gray-super-dark" />
										<nuxt-link
											:to="
												localePath(topNavbarLeft?.[0]?.Link?.cached_url || '/')
											"
											class="flex flex-col items-center"
											aria-label="Find store"
										>
											<span class="text-sm leading-none">
												{{ topNavbarLeft?.[0]?.text }}
											</span>
										</nuxt-link>
									</div>
									<div class="inline-flex gap-1 text-xs items-end">
										<EnvelopeIcon class="h-4 w-4 text-gray-super-dark" />
										<nuxt-link
											:to="
												localePath(topNavbarLeft?.[1]?.Link?.cached_url || '/')
											"
											class="flex flex-col items-center"
										>
											<span class="text-sm leading-none">
												{{ topNavbarLeft?.[1]?.text }}
											</span>
										</nuxt-link>
									</div>
									<div class="inline-flex gap-1 text-xs items-end">
										<HomeIcon class="h-4 w-4 text-gray-super-dark" />
										<p class="flex flex-col items-center">
											<span class="text-sm leading-none">
												{{ topNavbarLeft?.[2]?.text }}
											</span>
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import {
	XMarkIcon,
	Bars3Icon,
	ChevronLeftIcon,
	ChevronRightIcon
} from "@heroicons/vue/24/outline";
import { EnvelopeIcon, HomeIcon, PhoneIcon } from "@heroicons/vue/24/solid";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { CulturesNorce } from "~/types/components/header";
import type {
	MenuLink,
	ResolvedMenuNode,
	TopNavbarLeft
} from "~/types/storyblok/storyblok";

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	menuCategoriesNorce: Category[] | undefined;
	menuCategories: MenuLink[] | undefined;
	menu?: ResolvedMenuNode[];
}

const { topNavbarLeft, menuCategoriesNorce, menu } = defineProps<Props>();

// The Menu Node menu, or the old Norce categories in the same shape.
const {
	items: menuItems,
	usesNewMenu,
	hrefOf
} = useMenuWithFallback(() => ({ menu, menuCategoriesNorce }));

const localePath = useLocalePath();

const currentNavigationElement = ref<ResolvedMenuNode | null>();
const currentChildNavigationElement = ref<ResolvedMenuNode | null>();

const isSideMenuOpened = inject("isSideMenuOpened", ref(false));

const close = () => {
	isSideMenuOpened.value = false;
	currentNavigationElement.value = null;
	currentChildNavigationElement.value = null;
};

const goBack = () => {
	if (currentChildNavigationElement.value) {
		currentChildNavigationElement.value = null;
	} else if (currentNavigationElement.value) {
		currentNavigationElement.value = null;
	}
};

const firstNavigate = (value: ResolvedMenuNode) => {
	currentNavigationElement.value = value;
	if (!value.children.length) {
		close();
	}
};

const secondNavigate = (value: ResolvedMenuNode) => {
	currentChildNavigationElement.value = value;
	if (!value.children.length) {
		close();
	}
};
</script>
